import React, { useState, useRef, useEffect } from 'react'
import type { FC, ChangeEvent } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { createStyles, makeStyles } from '@material-ui/core/styles'

import {
  useGetOutreachDevelopmentsQuery,
  useCreateOutreachRequestsMutation,
} from '@homeviews/store'
import { mixpanelTrack } from '@homeviews/seo'
import { Card, Button, Select } from '@homeviews/shared-ui'
import {
  Typography,
  UploadFile,
  PageLayout,
  Checkbox,
} from '@homeviews/outreach'

const useStyles = makeStyles((theme) =>
  createStyles({
    card: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '20px',
    },
    customH2: {
      '.MuiTypography-h2&': {
        fontSize: '20px',
      },
    },
    rowWrapper: {
      display: 'flex',
      flexDirection: 'column',
    },
    row: {
      display: 'flex',
      columnGap: '20px',
      flexWrap: 'wrap',
    },
    selectWrapper: {
      width: '280px',
      marginBottom: '20px',
      '& .MuiFormLabel-root': { fontWeight: '700' },
      '& button': {
        padding: '10px 15px',
        marginBottom: '4px',
        fontSize: '14px',
      },
      fontWeight: 500,
    },
    selectPlaceholderWrapper: {
      color: 'blue',
    },
    deleteRowButton: {
      height: '40px',
      margin: '28px 0 0 0',
      color: theme.colors.red,
      borderColor: theme.colors.red,
    },
    addAnotherDevelopmentButton: {
      width: '280px',
      height: '40px',
      margin: '0 0 4px 0',
      color: theme.colors.grey600,
      borderColor: theme.colors.grey600,
    },
    checkboxAnchor: {
      color: theme.colors.grey650,
      fontWeight: 700,
    },
    submitWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
    },
    submitButton: {
      width: '260px',
      padding: '16px 84px',
      borderRadius: 0,
      fontWeight: 700,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      '&.MuiButton-root.Mui-disabled': {
        color: theme.colors.white,
        opacity: '25%',
      },
    },
    errorText: {
      marginTop: '10px',
      color: theme.colors.red,
    },
  }),
)

type SelectOption = {
  label: string
  value: string
}

type CreateRequestsErrorResponse = {
  data: {
    message: string
  }
  status: number
}

export const PageCreate: FC = () => {
  const { templateSlug } = useParams<{ templateSlug: string }>()
  const [fileChanged, setFileChanged] = useState(false)
  const classes = useStyles()
  const history = useHistory()

  const developmentIdsRef = useRef<Record<number, number>>({})
  const setDevelopmentId = (developmentId: number, rowIndex: number) => {
    developmentIdsRef.current[rowIndex] = developmentId
    validateSubmitRequirements()
  }

  const documentIdsRef = useRef<Record<number, number>>({})
  const setDocumentId = (documentId: number, rowIndex: number) => {
    documentIdsRef.current[rowIndex] = documentId
    validateSubmitRequirements()
  }
  const deleteDocumentId = (rowIndex: number) => {
    delete documentIdsRef.current[rowIndex]
    validateSubmitRequirements()
  }

  const [rowCount, setRowCount] = useState(1)
  const rowIndexes = () => [...Array.from(Array(rowCount).keys())] // e.g. [0, 1, 2]
  const handleOnClickAddAnotherDevelopmentButton = () => {
    setRowCount(rowCount + 1)
  }
  useEffect(() => {
    validateSubmitRequirements()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowCount])

  const handleOnClickDeleteRowButton = (rowIndex: number) => {
    delete developmentIdsRef.current[rowIndex]
    delete documentIdsRef.current[rowIndex]
    setRowCount(rowCount - 1)
  }

  const checkedCountRef = useRef(0)
  const handleOnChangeCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) checkedCountRef.current += 1
    else checkedCountRef.current -= 1
    validateSubmitRequirements()
  }

  const [submitEnabled, setSubmitEnabled] = useState(false)
  const validateSubmitRequirements = () => {
    // Validate all checkboxes are checked
    if (checkedCountRef.current !== 5) {
      setSubmitEnabled(false)
      return
    }
    // Validate each row of developmentId and documentId are set
    const allRowFieldsSet = rowIndexes().every(
      (rowIndex) =>
        documentIdsRef.current[rowIndex] !== undefined &&
        developmentIdsRef.current[rowIndex] !== undefined,
    )
    if (!allRowFieldsSet) {
      setSubmitEnabled(false)
      return
    }
    setSubmitEnabled(true) // Passed all validations, enable submit
  }

  const [errorString, setErrorString] = useState('')
  const [createRequests, { isLoading }] = useCreateOutreachRequestsMutation()
  const handleOnClickSubmitButton = async () => {
    mixpanelTrack('Outreach: Submit Send')
    const requestSchema = {
      template: templateSlug,
      payload: rowIndexes().map((rowIndex) => ({
        developmentId: developmentIdsRef.current[rowIndex],
        documentId: documentIdsRef.current[rowIndex],
      })),
    }
    try {
      await createRequests(requestSchema).unwrap()
      // If no error, then that's success and redirect to Outreach Home
      history.push('/outreach', { from: 'success' })
      mixpanelTrack('Outreach: Submit Success')
    } catch (err) {
      console.log('createRequests err=', err)
      const errResponse = err as CreateRequestsErrorResponse
      let errorStr = 'An unknown error has occurred'
      if (errResponse.data.message) {
        errorStr = errResponse.data.message
      }
      setErrorString(errorStr) // Show error to user
      mixpanelTrack('Outreach: Submit Error', { Message: errorStr })
    }
  }

  const { data, isFetching } = useGetOutreachDevelopmentsQuery(null, {
    refetchOnMountOrArgChange: true,
  })
  const selectOptions = () => {
    if (!data) return []
    // Pick only name & id from API call, and put in Select Options array structure
    const options = data.data.map((development) => ({
      label: development.name,
      value: development.id,
    }))
    // Return alphabetised (0A-Z first) based on label string
    return options.sort((a, b) => a.label.localeCompare(b.label))
  }
  const selectPlaceholder = () => {
    if (isFetching) return 'Loading developments...'
    if (data?.data?.length === 0) return 'No eligible developments'
    else return 'Please select'
  }

  return (
    <PageLayout>
      <Card className={classes.card}>
        <Typography variant="h2">Create New Resident Email</Typography>
        <Typography variant="body1">
          Upload resident emails, send and invite your residents to complete a
          HomeViews review.
        </Typography>
        <div className={classes.rowWrapper}>
          {rowIndexes().map((rowIndex) => (
            <div className={classes.row} key={rowIndex}>
              <div className={classes.selectWrapper}>
                <Select
                  label="Select Development"
                  placeholder={selectPlaceholder()}
                  options={selectOptions()}
                  onChange={({ label, value }: SelectOption) => {
                    mixpanelTrack('Outreach: Select Development', {
                      'Development Name': label,
                    })
                    setDevelopmentId(parseInt(value), rowIndex)
                  }}
                  withCheckboxes
                />
                <Typography variant="body2">
                  This is where the email will be sent
                </Typography>
              </div>
              <UploadFile
                inputId={`input-file-${rowIndex}`}
                showLabel={rowIndex === 0}
                wideUntilFileChanged={rowIndex === 0}
                fileChangedCallback={() => setFileChanged(true)}
                setDocumentIdCallback={(dId) => setDocumentId(dId, rowIndex)}
                deleteDocumentIdCallback={() => deleteDocumentId(rowIndex)}
              />
              {rowCount > 1 && rowCount === rowIndex + 1 && (
                <Button
                  customVariant="outlinedPrimary"
                  className={classes.deleteRowButton}
                  onClick={() => handleOnClickDeleteRowButton(rowIndex)}
                >
                  Delete row
                </Button>
              )}
            </div>
          ))}
        </div>
        {fileChanged && rowCount <= 9 && (
          <Button
            customVariant="outlinedPrimary"
            className={classes.addAnotherDevelopmentButton}
            onClick={handleOnClickAddAnotherDevelopmentButton}
          >
            Add another development
          </Button>
        )}
      </Card>
      <Card className={classes.card}>
        <Typography variant="h2" className={classes.customH2}>
          Email Guidelines
        </Typography>
        <Typography variant="body1">
          Please read the guidelines below regarding HomeViews emails. All
          checkboxes must be ticked for a compliant email.
        </Typography>
        <Checkbox handleOnChange={handleOnChangeCheckbox}>
          I agree to the{' '}
          <a
            className={classes.checkboxAnchor}
            href="https://www.homeviews.com/terms-of-use/"
            target="_blank"
            rel="noreferrer"
          >
            T&Cs
          </a>
        </Checkbox>
        <Checkbox handleOnChange={handleOnChangeCheckbox}>
          I agree that I am the data controller of this information and have the
          relative consent to conduct this email activity
        </Checkbox>
        <Checkbox handleOnChange={handleOnChangeCheckbox}>
          I understand that HomeViews will not contact residents who have left a
          review within the last 6 months
        </Checkbox>
        <Checkbox handleOnChange={handleOnChangeCheckbox}>
          I agree that I am only emailing current residents and the data has not
          been purchased, rented, appended or provided by a third party
        </Checkbox>
        <Checkbox handleOnChange={handleOnChangeCheckbox}>
          I agree not to spam residents with multiple review requests over a
          short period of time
        </Checkbox>
      </Card>
      <div className={classes.submitWrapper}>
        <Button
          customColor="primary"
          className={classes.submitButton}
          disabled={!submitEnabled}
          isLoading={isLoading}
          onClick={handleOnClickSubmitButton}
        >
          Send Email
        </Button>
        {errorString && <div className={classes.errorText}>{errorString}</div>}
      </div>
    </PageLayout>
  )
}
